import type { FC } from 'react';
import React from 'react';
import { Icon, DropDirection } from 'venn-ui-kit';
import { DropdownMenu, DropdownMenuItem } from '../../../dropdown-menu';
import type { Fund } from 'venn-api';

export type DropdownValue = number | string | boolean | symbol | Fund;
export const Mixed = Symbol('mixed');

interface DropdownProps {
  /**
   * List of options, with a value and a label
   */
  options: DropdownOption[];
  /**
   * Selected value. Must correspond to a value in options.
   */
  value?: DropdownValue;

  mixedLabel?: string;
  /**
   * Placeholder text to show when no value is selected
   */
  placeholder?: string;
  /**
   * Whether the dropdown opens up or down relative to the trigger. Defaults to down.
   */
  opensUp?: boolean;
  /**
   * If readonly, the value will be displayed but won't be changeable.
   */
  readonly?: boolean;
  /**
   * Fires when an option is selected. Doesn't fire if the selected option
   * is the same as the one already selected.
   */
  onChange?: (value: DropdownValue) => void;

  zIndex?: number;
}

export interface DropdownOption {
  displayLabel?: string;
  label?: string;
  value: DropdownValue;
}

const Dropdown: FC<React.PropsWithChildren<DropdownProps>> = ({
  options,
  value,
  readonly,
  onChange,
  mixedLabel,
  placeholder,
  opensUp,
  zIndex,
}) => {
  const isMixed = value === Mixed;
  const currentOption = options.find((option) => option.value === value);
  const readOnlyValue: string = isMixed
    ? mixedLabel || 'Mixed'
    : currentOption
      ? currentOption.displayLabel || currentOption.label || String(currentOption.value)
      : placeholder || '(unselected)';

  if (readonly) {
    return <div className="align-middle font-bold non-italic tracking-normal text-venn-grey-60">{readOnlyValue}</div>;
  }

  return (
    <DropdownMenu
      zIndex={zIndex}
      portal
      dropDirection={opensUp ? DropDirection.Up : DropDirection.Down}
      trigger={
        <div className="align-middle font-bold non-italic tracking-normal">
          {readOnlyValue}
          <Icon type="angle-down" className="ml-[5px] font-normal" />
        </div>
      }
    >
      {options.map((option) => (
        <DropdownMenuItem
          className="!text-venn-black"
          key={String(option.value)}
          onClick={() => {
            if (onChange && option.value !== value) {
              onChange(option.value);
            }
          }}
        >
          {option.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};

export default Dropdown;
